import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';

const SUPPORT_EMAIL = 'support@nutribot.com';

export default function SuspendedPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading, logout } = useAuthStore();

  // Wait for auth state to resolve before making any redirect decision
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="relative h-16 w-16">
          <div className="absolute inset-0 rounded-full border-4 border-gray-800" />
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-yellow-500 border-r-yellow-400 animate-spin" />
        </div>
      </div>
    );
  }

  // Unauthenticated users go to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but not suspended — redirect to their dashboard
  if (!user.is_suspended) {
    return <Navigate to={'/dashboard'} replace />;
  }

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">

      {/* Minimal header — no nav links, just branding */}
      <header className="border-b border-gray-800 px-6 py-4">
        <span className="text-xl font-bold">
          <span className="text-yellow-400">Nutri</span>
          <span className="text-white">Bot</span>
        </span>
      </header>

      {/* Main content — centered */}
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">

          {/* Icon */}
          <div className="w-20 h-20 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mx-auto mb-8">
            <i className="ri-shield-cross-line text-4xl text-red-400"></i>
          </div>

          {/* Heading */}
          <h1 className="text-3xl font-bold mb-3">
            {t('suspended.title')}
          </h1>
          <p className="text-gray-400 leading-relaxed mb-10">
            {t('suspended.description')}
          </p>

          {/* Support card */}
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mb-6 text-left">
            <p className="text-sm font-medium text-gray-300 mb-1">
              {t('suspended.contactLabel')}
            </p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t('suspended.emailSubject'))}`}
              className="flex items-center gap-3 group"
            >
              <div className="w-10 h-10 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-500/20 transition">
                <i className="ri-mail-line text-yellow-400 text-lg"></i>
              </div>
              <div>
                <p className="text-yellow-400 font-medium group-hover:text-yellow-300 transition">
                  {SUPPORT_EMAIL}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('suspended.emailHint')}
                </p>
              </div>
              <i className="ri-arrow-right-up-line text-gray-600 group-hover:text-yellow-400 transition ml-auto"></i>
            </a>
          </div>

          {/* Info note */}
          <p className="text-xs text-gray-600 mb-8">
            {t('suspended.note')}
          </p>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-gray-300 transition flex items-center gap-2 mx-auto"
          >
            <i className="ri-logout-box-line"></i>
            {t('suspended.logout')}
          </button>
        </div>
      </div>
    </div>
  );
}